<template>
  <ResponsiveLayout
    sticky-actions
    :progress="progress"
    :show-kaia-logo="false"
    :show-lang-picker="false"
  >
    <template #header_start><ButtonBack @click="emit('back')" /></template>

    <div class="kds-main-wrapper">
      <div class="kds-content-block">
        <p class="kds-typography-title-xlarge">
          {{ $t('eu__renewal_assessment__pgic__header') }}
        </p>
      </div>

      <div class="renewal-pgic__scale">
        <SelectionCard
          v-for="option in PGIC_OVERALL_CHANGE_OPTIONS"
          :key="option.key"
          :selected="selectedKey === option.key"
          :data-qa="`option-${option.key}`"
          @click="select(option)"
        >
          <template #leading>
            <span
              class="renewal-pgic__dot"
              :class="`renewal-pgic__dot--${option.color}`"
            />
          </template>
          <template #title>
            <span class="kds-typography-ui-strong-large">
              {{ option.score }} - {{ $t(option.label) }}
            </span>
          </template>
          <template #description>
            <span class="kds-typography-ui-subtle-medium">
              {{ $t(option.description) }}
            </span>
          </template>
        </SelectionCard>
      </div>
    </div>

    <template #actions>
      <BaseButton
        :text="$t('generic_action_continue')"
        :disabled="selectedKey == null"
        data-qa="submit"
        @click="handleNextEvent"
      />
    </template>
  </ResponsiveLayout>
</template>

<script setup>
import { computed } from 'vue'
import i18n from '@shared/localization'
import useRenewalAssessmentStore from '@shared/modules/assessment/stores/useRenewalAssessmentStore'
import { PGIC_OVERALL_CHANGE_OPTIONS } from '@shared/modules/assessment/config/renewalAssessmentOptions.js'
import ResponsiveLayout from '@shared/components/ResponsiveLayout.vue'
import ButtonBack from '@shared/components/ButtonBack.vue'
import BaseButton from '@shared/components/BaseButton.vue'
import SelectionCard from '@shared/components/SelectionCard.vue'

defineProps({
  progress: { type: Number, default: null },
  handleNextEvent: { type: Function, default: () => {} },
})

const emit = defineEmits(['back'])

const renewalAssessmentStore = useRenewalAssessmentStore()

const selectedKey = computed(
  () => renewalAssessmentStore.overallChange?.key ?? null,
)

// Store the label alongside the key: the API submits the copy the user saw. The
// score prefix stays out of it — the backend keeps the score on the option.
const select = (option) => {
  renewalAssessmentStore.overallChange = {
    key: option.key,
    label: i18n.global.t(option.label),
  }
}
</script>

<style lang="scss" scoped>
.renewal-pgic {
  &__scale {
    @include flex-container(column, flex-start, sm);
  }

  // The scale runs deep green (improved) through neutral to deep orange (worse),
  // so the dot reads as a severity indicator rather than decoration. Modifier
  // names mirror their KDS token, 7 down to 1.
  &__dot {
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background-color: $kds-color-background-strong;

    &--on-green {
      background-color: $kds-color-text-on-green;
    }

    &--green-accent {
      background-color: $kds-color-background-green-accent;
    }

    &--green-strong {
      background-color: $kds-color-background-green-strong;
    }

    &--strong {
      background-color: $kds-color-background-strong;
    }

    &--yellow-strong {
      background-color: $kds-color-background-yellow-strong;
    }

    &--orange-accent {
      background-color: $kds-color-background-orange-accent;
    }

    &--on-orange {
      background-color: $kds-color-text-on-orange;
    }
  }
}
</style>
